import { EoaAcademicConfigurationPayload } from '../../services/eoaConfigServices';
import { digitsOnlyError } from './fieldValidation';
import createFormRowId from './formRowId';
import persistNestedConfiguration from './persistNestedConfiguration';

export interface NamedRow {
  id: string;
  name: string;
}

export interface AliasRow {
  id: string;
  value: string;
}

export interface AliasMapping {
  id: string;
  periodName: string;
  aliases: AliasRow[];
}

export interface AcademicsForm {
  termCount: string;
  periodNames: NamedRow[];
  aliasMappings: AliasMapping[];
  crossSchoolEnrollment: string;
  studentTransfers: string;
  dualEnrollment: string;
}

const emptyAliasRow = (): AliasRow => ({ id: createFormRowId('alias'), value: '' });

const emptyPeriodName = (): NamedRow => ({ id: createFormRowId('period'), name: '' });

const emptyAliasMapping = (): AliasMapping => ({
  id: createFormRowId('mapping'),
  periodName: '',
  aliases: [emptyAliasRow()],
});

const INITIAL_ACADEMICS_FORM: AcademicsForm = {
  termCount: '',
  periodNames: [emptyPeriodName()],
  aliasMappings: [emptyAliasMapping()],
  crossSchoolEnrollment: '',
  studentTransfers: '',
  dualEnrollment: '',
};

const uniqueStrings = (values: string[]): string[] =>
  values.filter((value, index) => value.length > 0 && values.indexOf(value) === index);

const namedPeriodOptions = (form: AcademicsForm): string[] =>
  uniqueStrings(form.periodNames.map((period) => period.name.trim()));

const setTermCount = (form: AcademicsForm, value: string): AcademicsForm => ({ ...form, termCount: value });

const addPeriodName = (form: AcademicsForm): AcademicsForm => ({
  ...form,
  periodNames: [...form.periodNames, emptyPeriodName()],
});

const removePeriodName = (form: AcademicsForm, id: string): AcademicsForm => {
  const removed = form.periodNames.find((period) => period.id === id);
  const removedName = removed?.name.trim();
  return {
    ...form,
    periodNames: form.periodNames.filter((period) => period.id !== id),
    aliasMappings: removedName
      ? form.aliasMappings.filter((mapping) => mapping.periodName.trim() !== removedName)
      : form.aliasMappings,
  };
};

const updatePeriodName = (form: AcademicsForm, id: string, name: string): AcademicsForm => {
  const previous = form.periodNames.find((period) => period.id === id);
  const previousName = previous?.name.trim();
  const nextName = name.trim();
  return {
    ...form,
    periodNames: form.periodNames.map((period) => (period.id === id ? { ...period, name } : period)),
    aliasMappings: !previousName
      ? form.aliasMappings
      : nextName
        ? form.aliasMappings.map((mapping) =>
            mapping.periodName.trim() === previousName ? { ...mapping, periodName: name } : mapping
          )
        : form.aliasMappings.filter((mapping) => mapping.periodName.trim() !== previousName),
  };
};

const addAliasMapping = (form: AcademicsForm): AcademicsForm => ({
  ...form,
  aliasMappings: [...form.aliasMappings, emptyAliasMapping()],
});

const removeAliasMapping = (form: AcademicsForm, id: string): AcademicsForm => ({
  ...form,
  aliasMappings: form.aliasMappings.filter((mapping) => mapping.id !== id),
});

const updateAliasMappingPeriod = (form: AcademicsForm, id: string, periodName: string): AcademicsForm => ({
  ...form,
  aliasMappings: form.aliasMappings.map((mapping) => (mapping.id === id ? { ...mapping, periodName } : mapping)),
});

const addAlias = (form: AcademicsForm, mappingId: string): AcademicsForm => ({
  ...form,
  aliasMappings: form.aliasMappings.map((mapping) =>
    mapping.id === mappingId ? { ...mapping, aliases: [...mapping.aliases, emptyAliasRow()] } : mapping
  ),
});

const removeAlias = (form: AcademicsForm, mappingId: string, aliasId: string): AcademicsForm => ({
  ...form,
  aliasMappings: form.aliasMappings.map((mapping) =>
    mapping.id === mappingId
      ? { ...mapping, aliases: mapping.aliases.filter((alias) => alias.id !== aliasId) }
      : mapping
  ),
});

const updateAlias = (form: AcademicsForm, mappingId: string, aliasId: string, value: string): AcademicsForm => ({
  ...form,
  aliasMappings: form.aliasMappings.map((mapping) =>
    mapping.id === mappingId
      ? {
          ...mapping,
          aliases: mapping.aliases.map((alias) => (alias.id === aliasId ? { ...alias, value } : alias)),
        }
      : mapping
  ),
});

const toAcademicConfigurationPayload = (form: AcademicsForm): EoaAcademicConfigurationPayload => {
  const periodNames = form.periodNames.map((period) => period.name.trim()).filter(Boolean);
  const aliases: Record<string, string[]> = {};

  form.aliasMappings.forEach((mapping) => {
    const periodName = mapping.periodName.trim();
    if (!periodName) return;
    const values = mapping.aliases.map((alias) => alias.value.trim()).filter(Boolean);
    aliases[periodName] = [...(aliases[periodName] ?? []), ...values];
  });

  const payload: EoaAcademicConfigurationPayload = {
    academic_period_names: periodNames,
    academic_period_aliases: aliases,
    significant_cross_school_enrollment: form.crossSchoolEnrollment.trim(),
    how_are_student_transfers_handled: form.studentTransfers.trim(),
    how_do_dual_enrollment_courses_appear: form.dualEnrollment.trim(),
  };

  if (form.termCount !== '' && digitsOnlyError(form.termCount) === null) {
    payload.academic_term_count = Number(form.termCount);
  }

  return payload;
};

const areStringArraysEqual = (left: string[], right: string[]): boolean =>
  JSON.stringify(left) === JSON.stringify(right);

const areAliasMapsEqual = (
  left: Record<string, string[]> | undefined,
  right: Record<string, string[]> | undefined
): boolean => JSON.stringify(left ?? {}) === JSON.stringify(right ?? {});

const changedAcademicPayload = (previous: AcademicsForm, next: AcademicsForm): EoaAcademicConfigurationPayload => {
  const previousPayload = toAcademicConfigurationPayload(previous);
  const nextPayload = toAcademicConfigurationPayload(next);
  const patch: EoaAcademicConfigurationPayload = {};

  if (
    digitsOnlyError(next.termCount) === null &&
    previousPayload.academic_term_count !== nextPayload.academic_term_count
  ) {
    patch.academic_term_count = nextPayload.academic_term_count ?? null;
  }
  if (!areStringArraysEqual(previousPayload.academic_period_names ?? [], nextPayload.academic_period_names ?? [])) {
    patch.academic_period_names = nextPayload.academic_period_names;
  }
  if (!areAliasMapsEqual(previousPayload.academic_period_aliases, nextPayload.academic_period_aliases)) {
    patch.academic_period_aliases = nextPayload.academic_period_aliases;
  }
  if (previousPayload.significant_cross_school_enrollment !== nextPayload.significant_cross_school_enrollment) {
    patch.significant_cross_school_enrollment = nextPayload.significant_cross_school_enrollment;
  }
  if (previousPayload.how_are_student_transfers_handled !== nextPayload.how_are_student_transfers_handled) {
    patch.how_are_student_transfers_handled = nextPayload.how_are_student_transfers_handled;
  }
  if (previousPayload.how_do_dual_enrollment_courses_appear !== nextPayload.how_do_dual_enrollment_courses_appear) {
    patch.how_do_dual_enrollment_courses_appear = nextPayload.how_do_dual_enrollment_courses_appear;
  }

  return patch;
};

const hasInvalidAcademics = (form: AcademicsForm): boolean => digitsOnlyError(form.termCount) !== null;

const hasAcademicContent = (form: AcademicsForm): boolean => {
  const payload = toAcademicConfigurationPayload(form);
  return (
    payload.academic_term_count !== undefined ||
    (payload.academic_period_names?.length ?? 0) > 0 ||
    Object.keys(payload.academic_period_aliases ?? {}).length > 0 ||
    Boolean(payload.significant_cross_school_enrollment) ||
    Boolean(payload.how_are_student_transfers_handled) ||
    Boolean(payload.how_do_dual_enrollment_courses_appear)
  );
};

const academicsFromEoa = (data: Partial<EoaAcademicConfigurationPayload>): AcademicsForm => {
  const periodNames = (data.academic_period_names ?? []).map((name) => ({ id: createFormRowId('period'), name }));
  const aliasMappings = Object.entries(data.academic_period_aliases ?? {}).map(([periodName, aliases]) => ({
    id: createFormRowId('mapping'),
    periodName,
    aliases: (aliases.length > 0 ? aliases : ['']).map((value) => ({ id: createFormRowId('alias'), value })),
  }));

  return {
    termCount:
      data.academic_term_count === undefined || data.academic_term_count === null
        ? ''
        : String(data.academic_term_count),
    periodNames: periodNames.length > 0 ? periodNames : [emptyPeriodName()],
    aliasMappings: aliasMappings.length > 0 ? aliasMappings : [emptyAliasMapping()],
    crossSchoolEnrollment: data.significant_cross_school_enrollment ?? '',
    studentTransfers: data.how_are_student_transfers_handled ?? '',
    dualEnrollment: data.how_do_dual_enrollment_courses_appear ?? '',
  };
};

const persistAcademics = async ({
  form,
  previousForm,
  eoaId,
  exists,
  createAcademics,
  patchAcademics,
  createDraft,
}: {
  form: AcademicsForm;
  previousForm: AcademicsForm;
  eoaId: string | null;
  exists: boolean;
  createAcademics: (eoaId: string, payload: EoaAcademicConfigurationPayload) => Promise<unknown>;
  patchAcademics: (eoaId: string, payload: EoaAcademicConfigurationPayload) => Promise<unknown>;
  createDraft?: () => Promise<string | null>;
}): Promise<boolean> =>
  persistNestedConfiguration({
    form,
    previousForm,
    eoaId,
    exists,
    initialForm: INITIAL_ACADEMICS_FORM,
    hasContent: hasAcademicContent,
    changedPayload: changedAcademicPayload,
    create: createAcademics,
    patch: patchAcademics,
    createDraft,
  });

export {
  INITIAL_ACADEMICS_FORM,
  addAlias,
  addAliasMapping,
  addPeriodName,
  academicsFromEoa,
  changedAcademicPayload,
  hasAcademicContent,
  hasInvalidAcademics,
  namedPeriodOptions,
  persistAcademics,
  removeAlias,
  removeAliasMapping,
  removePeriodName,
  setTermCount,
  toAcademicConfigurationPayload,
  uniqueStrings,
  updateAlias,
  updateAliasMappingPeriod,
  updatePeriodName,
};
